import { useQuery, useQueryClient, useMutation } from "react-query";
import {
  getStoreLabel,
  uploadStoreLogo,
  removeStoreLogo,
  getStoreLogo,
} from "../services";
import { getStore, getUser, setStore } from "../utils/jwt";
import { useLocation, useSearchParams } from "react-router-dom";
import { useMemo } from "react";
import { ASSETS_BASE_URL } from "../constants";

export const useStore = (accountId) => {
  return useQuery(["storeLabel", accountId], () => getStoreLabel(accountId), {
    retry: 0,
    enabled: !!accountId,
  });
};

export const checkImage = async (path) => {
  const result = await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ path, status: "ok" });
    img.onerror = () => resolve({ path, status: "error" });
    img.src = path;
  });
  return result;
};

export const useStoreImage = (accountId) => {
  const imageUrl = `${ASSETS_BASE_URL}/image/proaccounts/${accountId}/logo.png`;
  return useQuery(["storeImage", accountId], () => checkImage(imageUrl), {
    retry: 0,
    enabled: !!accountId,
  });
};

export const useStoreLabel = (storeLabel) => {
  return useQuery(["storeLabel", storeLabel], () => getStoreLabel(storeLabel), {
    retry: 0,
    enabled: !!storeLabel,
    onSuccess: (data) => {
      if (
        data?.accountId &&
        data?.accountType === "pro" &&
        data?.hasRetailStore &&
        data?.active
      ) {
        setStore({
          accountId: data.accountId,
          accountName: data.accountName,
          accountEmpId: data.accountEmpId,
        });
      } else {
        localStorage.removeItem("store");
      }
    },
    onError: () => {
      localStorage.removeItem("store");
    },
  });
};

export const useUpdatedStore = () => {
  const { pathname } = useLocation();
  const newStore = useGetStoreWithQuery();

  const store = useMemo(() => {
    if (pathname === "/login") {
      return {};
    }
    if (newStore) {
      return newStore;
    }
    return getStore() || {};
  }, [pathname, newStore]);

  return store;
};

export const useGetStoreWithQuery = () => {
  const [searchParams] = useSearchParams();
  const storeLabel = searchParams.get("store");
  const { data, isSuccess } = useStoreLabel(storeLabel);
  if (isSuccess) {
    return data;
  }
  return null;
};

export const useUploadStoreLogo = (handleError) => {
  const queryClient = useQueryClient();
  return useMutation("updateStoreLogo", uploadStoreLogo, {
    onSuccess: () => {
      const user = getUser();
      // Invalidate both storeLogo and storeImage queries
      queryClient.invalidateQueries(["storeLogo"], {
        accountId: user?.accountId,
      });
      queryClient.invalidateQueries(["storeImage"], {
        accountId: user?.accountId,
      });
    },
    onError: (err) => {
      handleError({ isError: true, errorMsg: err.message });
    },
  });
};

export const useRemoveStoreLogo = (handleError) => {
  const queryClient = useQueryClient();
  return useMutation("removeStoreLogo", removeStoreLogo, {
    onSuccess: () => {
      const user = getUser();
      queryClient.invalidateQueries(["storeLogo"], {
        accountId: user?.accountId,
      });
    },
    onError: (err) => {
      handleError({ isError: true, errorMsg: err.message });
    },
  });
};

export const useGetStoreLogo = (accountId) => {
  return useQuery(["storeLogo", accountId], () => getStoreLogo(accountId), {
    retry: 0,
    enabled: !!accountId,
  });
};
